/**
 * Navigation Component
 * Global navigation bar with home button accessible from all pages
 * Optimized for mobile-first responsive design
 */

import React, { useState, useEffect } from 'react';
import { AlignJustify, Home, LogOut, LogIn, Sun, Moon, X, User } from 'lucide-react';
import { ResponsiveNav, TouchIconButton, TabletUp, useResponsive } from './MobileResponsive';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { LanguageSwitcher } from './LanguageSwitcher';
import { messageService } from '../../services/messageService';
import { updatePwaBadgeCount } from '../../utils/pwaBadge';
import { AppBrandLogo } from './AppBrandLogo';
import { LearnerHomeDesignToggle } from '../dashboard/LearnerHomeDesignBar';
import { SwitchSpinner } from './SwitchSpinner';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';

export interface NavigationProps {
  user?: any;
  currentView?: string;
  onHomeClick?: () => void;
  onLogout?: () => void;
  onProfileClick?: () => void;
  onLoginClick?: () => void;
  className?: string;
  sideNavOpen?: boolean;
  onSideNavToggle?: () => void;
  sideNavPanelId?: string;
}

const brandLogoButtonClass =
  'flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full border-2 border-white/40 bg-gradient-to-t from-slate-600/90 via-slate-500/72 to-slate-300/45 shadow-lg ring-2 ring-amber-400/35 backdrop-blur-md transition-all hover:scale-105 hover:from-slate-600 hover:via-slate-500 hover:to-slate-300/55 active:scale-95 dark:border-white/30 dark:from-white/15 dark:via-white/15 dark:to-white/15 dark:ring-amber-400/25 dark:hover:from-white/20 dark:hover:via-white/20 dark:hover:to-white/20';

/** Header icon controls — no focus ring / tap halo (matches nav toggle). */
const headerChromeIconBtn =
  'header-chrome-icon-btn flex shrink-0 items-center justify-center border-0 bg-transparent p-0 shadow-none outline-none ring-0 transition-opacity [-webkit-tap-highlight-color:transparent] hover:opacity-75 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:opacity-90';

function LearnerNavToggleIcon({ open }: { open: boolean }) {
  return (
    <span className="relative block h-5 w-5" aria-hidden>
      <AlignJustify
        className={`absolute inset-0 h-5 w-5 transition-all duration-300 ease-out ${
          open ? 'scale-75 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100'
        }`}
      />
      <X
        className={`absolute inset-0 h-5 w-5 transition-all duration-300 ease-out ${
          open ? 'scale-100 rotate-0 opacity-100' : 'scale-75 -rotate-90 opacity-0'
        }`}
      />
    </span>
  );
}

function BrandLogoButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      onClick={onClick}
      className={brandLogoButtonClass}
      aria-label={t('goToHome')}
      title={t('goToHome')}
    >
      <AppBrandLogo className="h-[3.625rem] w-[3.625rem]" alt="" />
    </button>
  );
}

function UserSymbolicAvatar({ size = 'lg' }: { size?: 'lg' | 'sm' }) {
  const iconClass = size === 'lg' ? 'h-9 w-9' : 'h-5 w-5';
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-purple-400 to-pink-400">
      <User className={`${iconClass} text-white`} aria-hidden />
    </div>
  );
}

function ProfileAvatarButton({
  user,
  onClick,
  isMobile,
}: {
  user: { profilePhoto?: string; firstName?: string; email?: string };
  onClick: () => void;
  isMobile: boolean;
}) {
  const { t } = useTranslation('common');
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    setPhotoFailed(false);
  }, [user.profilePhoto]);

  return (
    <button
      type="button"
      data-tour-id="header-profile"
      onClick={onClick}
      className={`${headerChromeIconBtn} overflow-hidden rounded-full border-2 border-white/40 bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-md dark:from-slate-500 dark:to-slate-600 ${
        isMobile ? 'h-11 w-11' : 'h-[3.25rem] w-[3.25rem]'
      }`}
      aria-label={t('goToProfile')}
      title={t('goToProfile')}
    >
      {user.profilePhoto && !photoFailed ? (
        <img
          key={user.profilePhoto}
          src={user.profilePhoto}
          alt=""
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setPhotoFailed(true)}
        />
      ) : (
        <UserSymbolicAvatar size="sm" />
      )}
    </button>
  );
}

export const Navigation: React.FC<NavigationProps> = ({
  user,
  currentView,
  onHomeClick,
  onLogout,
  onProfileClick,
  onLoginClick,
  className = '',
  sideNavOpen = false,
  onSideNavToggle,
  sideNavPanelId,
}) => {
  const { isMobile } = useResponsive();
  const [homeDesign] = useLearnerHomeDesign();
  const { toggleTheme, isDark } = useTheme();
  const [themePending, setThemePending] = useState(false);
  const { t } = useTranslation('common');
  const { t: tNav } = useTranslation('nav');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [unreadCount, setUnreadCount] = useState(0);
  const isLearnerChrome = Boolean(user);
  const goHome = onHomeClick || (() => {});
  const displayName = user?.firstName || user?.email?.split('@')[0] || tNav('learnerDefault');
  const userClusterClass = isRtl
    ? 'flex flex-row-reverse items-center gap-2 sm:gap-3'
    : 'flex items-center gap-2 sm:gap-3';

  // Load unread count
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    const loadUnreadCount = async () => {
      try {
        const count = await messageService.getUnreadCount();
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to load unread count:', error);
      }
    };

    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);

    const handleMessageRead = () => {
      loadUnreadCount();
      setTimeout(() => {
        loadUnreadCount();
      }, 500);
    };
    window.addEventListener('messageRead', handleMessageRead);

    return () => {
      clearInterval(interval);
      window.removeEventListener('messageRead', handleMessageRead);
    };
  }, [user]);

  useEffect(() => {
    void updatePwaBadgeCount(unreadCount);
  }, [unreadCount]);

  const onThemeClick = () => {
    if (themePending) return;
    setThemePending(true);
    void toggleTheme().finally(() => setThemePending(false));
  };

  const themeToggle = (
    <button
      type="button"
      data-tour-id="header-theme-toggle"
      onClick={onThemeClick}
      aria-busy={themePending}
      aria-disabled={themePending}
      className={`${headerChromeIconBtn} ${isLearnerChrome ? 'h-7 w-11' : 'h-11 w-11'} text-[#0a0a0a] dark:text-white ${themePending ? 'cursor-default' : ''}`}
      aria-label={isDark ? t('lightMode') : t('darkMode')}
      title={isDark ? t('lightMode') : t('darkMode')}
    >
      {themePending ? <SwitchSpinner /> : isDark ? <Sun className="h-5 w-5" aria-hidden /> : <Moon className="h-5 w-5" aria-hidden />}
    </button>
  );

  const loginButton = (
    <TouchIconButton
      icon={<LogIn className="w-4 h-4" />}
      onClick={onLoginClick || (() => {})}
      label={t('loginToAccount')}
      variant="ghost"
      className="text-[#0a0a0a] dark:text-gray-300 hover:text-[#0a0a0a] dark:hover:text-white hover:bg-black/5 dark:hover:bg-gray-800/80"
    />
  );

  const userSection = (
    <div className={userClusterClass}>
      <div className={isLearnerChrome ? 'flex h-16 flex-col items-center justify-center' : undefined}>
        {themeToggle}
        {isLearnerChrome ? <LearnerHomeDesignToggle /> : null}
      </div>
      {!user ? (
        <LanguageSwitcher
          variant="compact"
          className="shrink-0"
          dataTourId="header-language-toggle"
        />
      ) : null}

      {user ? (
        <>
          {!isMobile ? (
            <div className="min-w-0 text-start me-1 sm:me-2">
              <p className="text-xs text-[#0a0a0a]/75 dark:text-gray-400">{tNav('welcomeBack')}</p>
              <p className="truncate text-xs font-semibold text-[#0a0a0a] dark:text-gray-200 sm:max-w-[120px] sm:text-sm lg:max-w-none">
                {tNav('hiUser', { name: displayName })}
              </p>
            </div>
          ) : null}

          <div className="relative">
            <ProfileAvatarButton
              user={user}
              onClick={onProfileClick || (() => {})}
              isMobile={isMobile}
            />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -start-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-white dark:border-gray-900 shadow-lg z-10 pointer-events-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>

          <TouchIconButton
            icon={<LogOut className="w-4 h-4" />}
            onClick={onLogout || (() => {})}
            label={t('logout')}
            variant="ghost"
            dataTourId="header-logout"
            className="text-[#0a0a0a] dark:text-gray-300 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          />

          <TabletUp>
            <span
              onClick={onLogout}
              className="text-sm text-[#0a0a0a] dark:text-gray-300 hover:text-red-700 dark:hover:text-red-400 cursor-pointer transition-colors"
            >
              {t('logout')}
            </span>
          </TabletUp>
        </>
      ) : (
        loginButton
      )}
    </div>
  );

  const sideNavToggle = isLearnerChrome && onSideNavToggle ? (
    <button
      type="button"
      data-learner-nav-toggle
      data-tour-id="side-nav-menu"
      onClick={onSideNavToggle}
      className={`${headerChromeIconBtn} h-11 w-11 text-[#0a0a0a] dark:text-white`}
      aria-expanded={sideNavOpen}
      aria-controls={sideNavPanelId}
      aria-label={sideNavOpen ? tNav('closeMenu') : tNav('openMenu')}
      title={sideNavOpen ? tNav('closeMenuShort') : tNav('menuTitle')}
    >
      <LearnerNavToggleIcon open={sideNavOpen} />
    </button>
  ) : null;

  const simpleHome = homeDesign === 'mme';
  const brandWord = (
    <span className="truncate text-[15px] font-semibold tracking-tight text-[#1c1917] dark:text-gray-100 sm:text-xl md:text-2xl">
      {t('appName')}
    </span>
  );

  const homeButton =
    currentView !== 'home' ? (
      <TouchIconButton
        icon={<Home className="w-4 h-4 sm:w-5 sm:h-5" />}
        onClick={goHome}
        label={t('goToHome')}
        variant="ghost"
        className="text-[#0a0a0a] dark:text-gray-300 hover:text-[#0a0a0a] dark:hover:text-white hover:bg-black/5 dark:hover:bg-gray-800/80"
      />
    ) : null;

  return (
    <ResponsiveNav
      className={className}
      chrome={isLearnerChrome ? (simpleHome ? 'learner-simple' : 'learner') : 'default'}
    >
      {isLearnerChrome ? (
        <div
          dir="ltr"
          className={`relative flex w-full items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}
        >
          <div className={`flex min-w-0 shrink-0 items-center gap-0.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
            {sideNavToggle}
            {isMobile ? brandWord : null}
          </div>
          {!isMobile ? (
            <div
              className={`flex min-w-0 flex-1 items-center gap-2 ${
                isRtl ? 'justify-end pr-1 sm:pr-2' : 'pl-1 sm:pl-2'
              }`}
            >
              {brandWord}
              {homeButton}
            </div>
          ) : (
            <div className="min-w-0 flex-1" />
          )}
          <div className="shrink-0">{userSection}</div>
        </div>
      ) : (
        <div
          dir="ltr"
          className={`flex w-full items-center justify-between ${isRtl ? 'flex-row-reverse' : ''}`}
        >
          <div className="flex items-center space-x-2 sm:space-x-4">
            <BrandLogoButton onClick={goHome} />

            <h1 className="hidden sm:block text-base sm:text-xl md:text-2xl font-semibold text-[#0a0a0a] dark:text-gray-100 tracking-tight">
              {t('appName')}
            </h1>

            {currentView !== 'home' && (
              <TouchIconButton
                icon={<Home className="w-4 h-4 sm:w-5 sm:h-5" />}
                onClick={goHome}
                label={t('goToHome')}
                variant="ghost"
                className="text-[#0a0a0a] dark:text-gray-300 hover:text-[#0a0a0a] dark:hover:text-white hover:bg-black/5 dark:hover:bg-gray-800/80"
              />
            )}
          </div>

          {userSection}
        </div>
      )}
    </ResponsiveNav>
  );
};

export default Navigation;
